import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, LayoutDashboard, Users, Inbox, Megaphone, HandCoins, Wallet, LogOut, ShieldCheck } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const items = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Standup", tid: "nav-dashboard", tip: "Your daily briefing — start here every morning." },
  { to: "/crew", icon: Users, label: "The Crew", tid: "nav-crew", tip: "Meet your 3 AI employees + CEO." },
  { to: "/approvals", icon: Inbox, label: "Approvals", tid: "nav-approvals", tip: "High-risk actions wait here for your yes/no." },
  { to: "/marketer", icon: Megaphone, label: "Marketer", tid: "nav-marketer", tip: "Draft posts and generate visuals." },
  { to: "/sales", icon: HandCoins, label: "Sales", tid: "nav-sales", tip: "Lightweight CRM + outreach drafts." },
  { to: "/accountant", icon: Wallet, label: "Accountant", tid: "nav-accountant", tip: "Calm money view. Never files anything." },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const isAccountant = loc.pathname.startsWith("/accountant");

  return (
    <TooltipProvider delayDuration={200}>
      <div className={"min-h-screen " + (isAccountant ? "accountant-mode" : "aurora relative overflow-hidden")}>
        {!isAccountant && (
          <>
            <div className="pointer-events-none absolute -top-20 -left-20 w-72 h-72 bg-neon-pink/40 blur-3xl slime-blob" />
            <div className="pointer-events-none absolute top-40 -right-16 w-72 h-72 bg-neon-cyan/30 blur-3xl slime-blob" />
            <div className="pointer-events-none absolute bottom-0 left-1/3 w-72 h-72 bg-neon-purple/40 blur-3xl slime-blob" />
          </>
        )}

        <aside className={"fixed inset-y-0 left-0 w-64 p-5 " + (isAccountant ? "bg-white border-r border-slate-200" : "glass border-r border-white/10 z-10")} data-testid="app-sidebar">
          <Link to="/dashboard" className="flex items-center gap-2 mb-8" data-testid="brand-link">
            <Sparkles className={"w-6 h-6 " + (isAccountant ? "text-indigo-500" : "text-neon-pink animate-pulse")} />
            <span className={"font-display text-2xl font-bold " + (isAccountant ? "text-slate-900" : "chrome-text")}>SoloCrew</span>
          </Link>

          <nav className="flex flex-col gap-1">
            {items.map((it) => (
              <Tooltip key={it.to}>
                <TooltipTrigger asChild>
                  <NavLink
                    to={it.to}
                    data-testid={it.tid}
                    className={({ isActive }) =>
                      "flex items-center gap-3 px-3 py-2.5 rounded-xl font-semibold transition-colors " +
                      (isActive
                        ? isAccountant ? "bg-indigo-50 text-indigo-700" : "bg-white/10 text-white neon-glow-pink"
                        : isAccountant ? "text-slate-600 hover:bg-slate-100" : "text-white/70 hover:text-white hover:bg-white/5")
                    }
                  >
                    <it.icon className="w-5 h-5" />
                    {it.label}
                  </NavLink>
                </TooltipTrigger>
                <TooltipContent side="right" className="bg-slate-900 text-white border-neon-pink/50">
                  {it.tip}
                </TooltipContent>
              </Tooltip>
            ))}

            {user?.is_admin && (
              <NavLink
                to="/admin/waitlist"
                data-testid="nav-admin-waitlist"
                className={({ isActive }) =>
                  "flex items-center gap-3 px-3 py-2.5 rounded-xl font-semibold mt-2 border transition-colors " +
                  (isActive
                    ? isAccountant ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-neon-cyan/15 text-neon-cyan border-neon-cyan/30"
                    : isAccountant ? "text-slate-600 border-slate-200 hover:bg-slate-100" : "text-white/70 border-white/10 hover:bg-white/5 hover:text-white")
                }
              >
                <ShieldCheck className="w-5 h-5" />
                Waitlist admin
              </NavLink>
            )}
          </nav>

          <div className="absolute bottom-5 left-5 right-5">
            <div className={"flex items-center gap-3 p-3 rounded-2xl " + (isAccountant ? "bg-slate-50" : "glass")}>
              <Avatar className="w-9 h-9">
                <AvatarImage src={user?.picture} />
                <AvatarFallback>{user?.name?.[0] ?? "?"}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className={"text-sm font-semibold truncate " + (isAccountant ? "text-slate-900" : "text-white")}>{user?.name}</div>
                <div className={"text-xs truncate " + (isAccountant ? "text-slate-500" : "text-white/60")}>{user?.email}</div>
              </div>
              <button
                data-testid="logout-btn"
                onClick={async () => { await logout(); nav("/"); }}
                className={"p-2 rounded-lg " + (isAccountant ? "hover:bg-slate-200" : "hover:bg-white/10 text-white")}
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </aside>

        <main className="ml-64 relative">{children}</main>
      </div>
    </TooltipProvider>
  );
}
